// FormPagesDrawer — "Manage pages", opened from the builder toolbar's first
// icon button: one card per page (number badge · inline rename Input ·
// "{{count}} fields (· currently open)" · eye / move / duplicate / delete)
// plus a dashed Add page button. Edits are LIVE (confirmed) — every action
// hits the real builder state immediately and the drawer has no footer, only
// the X close — so Delete goes through a danger ConfirmModal (an unconfirmed
// live delete would be unrecoverable). The eye opens that page in the canvas
// (moves the "currently open" marker); the drawer stays open throughout.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, Drawer, Icon, IconButton, Input } from '@/components/common';
import type { BuilderPage } from './utils/builderModel';

interface FormPagesDrawerProps {
  open: boolean;
  onClose: () => void;
  pages: BuilderPage[];
  activePageId: string;
  /** Eye — make this page the one open in the canvas. */
  onOpenPage: (pageId: string) => void;
  onRenamePage: (pageId: string, title: string) => void;
  onMovePage: (pageId: string, dir: -1 | 1) => void;
  onDuplicatePage: (pageId: string) => void;
  onDeletePage: (pageId: string) => void;
  onAddPage: () => void;
}

const FormPagesDrawer = ({
  open,
  onClose,
  pages,
  activePageId,
  onOpenPage,
  onRenamePage,
  onMovePage,
  onDuplicatePage,
  onDeletePage,
  onAddPage,
}: FormPagesDrawerProps) => {
  const { t } = useTranslation();
  const [confirmPageId, setConfirmPageId] = useState<string | null>(null);

  const pageName = (p: BuilderPage) =>
    p.properties.basicProperties.pageTitle || t('forms_builder_untitled_page');
  const confirmIndex = pages.findIndex((p) => p.id === confirmPageId);
  const confirmPage = confirmIndex >= 0 ? pages[confirmIndex] : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('forms_builder_pages_title')}
      subtitle={t('forms_builder_pages_subtitle')}
      className='form-pages'
    >
      <div className='form-pages__list'>
        {pages.map((p, i) => {
          const active = p.id === activePageId;
          return (
            <div
              key={p.id}
              className={['form-pages__row', active && 'form-pages__row--active']
                .filter(Boolean)
                .join(' ')}
            >
              <span className='form-pages__num' aria-hidden='true'>
                {i + 1}
              </span>
              <div className='form-pages__main'>
                <Input
                  value={p.properties.basicProperties.pageTitle}
                  placeholder={t('forms_builder_untitled_page')}
                  aria-label={t('forms_builder_pages_rename')}
                  onChange={(e) => onRenamePage(p.id, e.target.value)}
                  size='sm'
                />
                <span className='form-pages__meta'>
                  {t('forms_builder_pages_count', { count: p.fields.length })}
                  {active && (
                    <span className='form-pages__current'>
                      {' · '}
                      {t('forms_builder_pages_current')}
                    </span>
                  )}
                </span>
              </div>
              <div className='form-pages__actions'>
                <IconButton
                  icon='eye'
                  variant='ghost'
                  size='sm'
                  className={active ? 'form-pages__open--active' : undefined}
                  aria-label={t('forms_builder_pages_open')}
                  aria-pressed={active}
                  onClick={() => onOpenPage(p.id)}
                />
                <IconButton
                  icon='chevron-up'
                  variant='ghost'
                  size='sm'
                  aria-label={t('forms_builder_field_move_up')}
                  disabled={i === 0}
                  onClick={() => onMovePage(p.id, -1)}
                />
                <IconButton
                  icon='chevron-down'
                  variant='ghost'
                  size='sm'
                  aria-label={t('forms_builder_field_move_down')}
                  disabled={i === pages.length - 1}
                  onClick={() => onMovePage(p.id, 1)}
                />
                <IconButton
                  icon='copy'
                  variant='ghost'
                  size='sm'
                  aria-label={t('forms_builder_field_duplicate')}
                  onClick={() => onDuplicatePage(p.id)}
                />
                <IconButton
                  icon='trash-2'
                  variant='ghost'
                  size='sm'
                  className='form-builder-field__delete'
                  aria-label={t('forms_builder_page_delete')}
                  disabled={pages.length <= 1}
                  onClick={() => setConfirmPageId(p.id)}
                />
              </div>
            </div>
          );
        })}

        <button type='button' className='form-pages__add' onClick={onAddPage}>
          <Icon name='plus' size={16} />
          {t('forms_builder_add_page')}
        </button>
      </div>

      <ConfirmModal
        open={Boolean(confirmPage)}
        onClose={() => setConfirmPageId(null)}
        onConfirm={() => {
          if (confirmPage) onDeletePage(confirmPage.id);
          setConfirmPageId(null);
        }}
        tone='danger'
        icon='trash-2'
        title={t('forms_builder_page_delete_title')}
        description={
          confirmPage
            ? t('forms_builder_page_delete_desc', {
                name: pageName(confirmPage),
                count: confirmPage.fields.length,
              })
            : ''
        }
        confirmLabel={t('forms_builder_page_delete')}
        cancelLabel={t('button_cancel')}
        confirmVariant='danger'
      />
    </Drawer>
  );
};

export default FormPagesDrawer;
